import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "../../../utils/demoToast";
import useApi from "../../../hooks/useApi";
import useResponsive from "../../../hooks/useBreakpoint";
import { usePageTitle } from "../../../hooks/useMeta";
import Button from "../../buttons/Button";
import DropdownButton from "../../buttons/DropdownButton";
import Modal from "../../utils/Modal";
import Spinner from "../../utils/Spinner";
import Collapse from "../../utils/Collapse";
import Card from "../../utils/Card";
import Tag from "../../utils/Tag";
import Form from "../../inputs/Form";
import Checkbox from "react-custom-checkbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faCopy,
  faCheck,
  faChair,
  faEllipsisVertical,
  faEllipsis,
  faTableCellsLarge,
  faList,
} from "@fortawesome/pro-light-svg-icons";
import {
  getTableOverview,
  createRoom,
  updateRoom,
  deleteRoom,
  createTable,
  updateTable,
  deleteTable,
  duplicateTable,
  moveTable,
  reorderTablesInRoom,
  reorderRooms,
} from "../../../services/tableService";

// Sortable wrapper for table cards/rows
const SortableTableItem = ({ id, roomId, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { type: "table", roomId } });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : undefined,
  };

  return children({ setNodeRef, style, attributes, listeners, isDragging });
};

// Droppable zone wrapping each room's Collapse (enables cross-room drops even when collapsed)
const DroppableRoom = ({ roomId, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `container-room-${roomId ?? "none"}`,
    data: { type: "room-droppable", roomId },
  });

  return (
    <div
      ref={setNodeRef}
      className={`droppable-room ${isOver ? "droppable-room--over" : ""}`}
    >
      {children}
    </div>
  );
};

// Sortable wrapper for room collapses
const SortableRoomItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { type: "room" } });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return children({ setNodeRef, style, attributes, listeners, isDragging });
};

const RestaurantTables = ({ tKey = "dashboard.tables." }) => {
  usePageTitle("restaurantTables");
  const { t } = useTranslation();
  const { isLtTablet } = useResponsive();
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const controlledRestaurantId = useSelector(
    (state) => state.admin.controlledRestaurantId,
  );

  const activeRestaurantId = controlledRestaurantId || restaurantId;

  const [viewMode, setViewMode] = useState("card");
  const effectiveViewMode = isLtTablet() ? "card" : viewMode;
  const [hideInactive, setHideInactive] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [expandedRooms, setExpandedRooms] = useState({});
  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [roomForm, setRoomForm] = useState({ name: "", description: "" });
  const [tableForm, setTableForm] = useState({
    name: "",
    description: "",
    numberOfPlaces: 2,
    isActive: true,
    roomId: null,
  });
  const [isRoomFormValid, setIsRoomFormValid] = useState(false);
  const [isTableFormValid, setIsTableFormValid] = useState(false);
  const [activeDragTable, setActiveDragTable] = useState(null);
  const expandTimerRef = useRef(null);

  const { request: fetchOverviewRequest, loading: overviewLoading } =
    useApi(getTableOverview);
  const { request: createRoomRequest, loading: createRoomLoading } =
    useApi(createRoom);
  const { request: updateRoomRequest, loading: updateRoomLoading } =
    useApi(updateRoom);
  const { request: deleteRoomRequest, loading: deleteRoomLoading } =
    useApi(deleteRoom);
  const { request: createTableRequest, loading: createTableLoading } =
    useApi(createTable);
  const { request: updateTableRequest, loading: updateTableLoading } =
    useApi(updateTable);
  const { request: deleteTableRequest, loading: deleteTableLoading } =
    useApi(deleteTable);
  const { request: duplicateTableRequest, loading: duplicateTableLoading } =
    useApi(duplicateTable);
  const { request: moveTableRequest } = useApi(moveTable);
  const { request: reorderTablesRequest } = useApi(reorderTablesInRoom);
  const { request: reorderRoomsRequest } = useApi(reorderRooms);

  // Sorted rooms: respect room.order, unassigned (roomId null) always last
  const sortedRooms = useMemo(() => {
    return [...rooms].sort((a, b) => {
      const aIsUnassigned = a.roomId === null;
      const bIsUnassigned = b.roomId === null;
      if (aIsUnassigned && !bIsUnassigned) return 1;
      if (!aIsUnassigned && bIsUnassigned) return -1;
      return (a.order ?? 0) - (b.order ?? 0);
    });
  }, [rooms]);

  // Real rooms only (for dropdown and room reorder; exclude pseudo-room)
  const realRooms = useMemo(
    () => sortedRooms.filter((r) => r.roomId != null),
    [sortedRooms],
  );

  // Room form fields definition
  const roomFields = useMemo(
    () => [
      {
        name: "name",
        placeholder: t(`${tKey}roomNamePlaceholder`),
        type: "text",
        class: "w-100",
        validate: (value) => {
          if (!value || !value.trim())
            return t(`${tKey}errors.roomNameRequired`);
          return "";
        },
      },
      {
        name: "description",
        placeholder: t(`${tKey}roomDescriptionPlaceholder`),
        type: "textarea",
        class: "w-100",
        rows: 3,
      },
    ],
    [t, tKey],
  );

  const fetchData = useCallback(async () => {
    if (!activeRestaurantId) return;
    try {
      const overviewResponse = await fetchOverviewRequest(activeRestaurantId);
      const roomsList = overviewResponse.data?.rooms || [];
      setRooms(roomsList);
      const expanded = {};
      roomsList.forEach((room) => {
        const key = room.roomId === null ? "noRoom" : room.roomId;
        expanded[key] = (room.tables?.length ?? 0) > 0;
      });
      setExpandedRooms(expanded);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t(`${tKey}errors.fetchRooms`));
    }
  }, [activeRestaurantId, fetchOverviewRequest, t, tKey]);

  // Table form fields definition
  const tableFields = useMemo(
    () => [
      {
        name: "name",
        placeholder: t(`${tKey}tableNamePlaceholder`),
        type: "text",
        class: "w-100",
        validate: (value) => {
          if (!value || !value.trim())
            return t(`${tKey}errors.tableNameRequired`);
          return "";
        },
      },
      {
        name: "description",
        placeholder: t(`${tKey}tableDescriptionPlaceholder`),
        type: "textarea",
        class: "w-100",
        rows: 1,
      },
      {
        name: "numberOfPlaces",
        placeholder: t(`${tKey}numberOfPlacesPlaceholder`),
        type: "number",
        class: "w-50 pr-1",
        validate: (value) => {
          if (!value || value < 1)
            return t(`${tKey}errors.numberOfPlacesRequired`);
          if (value > 50) return t(`${tKey}errors.numberOfPlacesMax`);
          return "";
        },
      },
      {
        name: "roomId",
        placeholder: t(`${tKey}room`),
        type: "dropdown",
        class: "w-50 pl-1",
        isClearable: true,
        options: [
          { value: null, label: t(`${tKey}noRoom`) },
          ...realRooms.map((room) => ({
            value: room.roomId,
            label: room.name,
          })),
        ],
        onAdd: async (inputValue) => {
          try {
            const response = await createRoomRequest(activeRestaurantId, {
              name: inputValue,
            });
            const newRoom = response.data;
            toast.success(t(`${tKey}success.roomCreated`));
            await fetchData();
            setTableForm((prev) => ({
              ...prev,
              roomId: { value: newRoom.roomId, label: newRoom.name },
            }));
          } catch (error) {
            console.error("Error creating room inline:", error);
            toast.error(t(`${tKey}errors.saveRoom`));
          }
        },
      },
      {
        name: "isActive",
        label: t(`${tKey}isActive`),
        type: "checkbox",
        class: "w-100",
      },
    ],
    [t, tKey, realRooms, createRoomRequest, activeRestaurantId, fetchData],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleRoom = (roomId) => {
    setExpandedRooms((prev) => ({
      ...prev,
      [roomId]: !prev[roomId],
    }));
  };

  // Room handlers
  const openAddRoomModal = () => {
    setEditingRoom(null);
    setRoomForm({ name: "", description: "" });
    setIsRoomModalOpen(true);
  };

  const openEditRoomModal = (room) => {
    setEditingRoom(room);
    setRoomForm({ name: room.name, description: room.description || "" });
    setIsRoomModalOpen(true);
  };

  const handleSaveRoom = async () => {
    if (!isRoomFormValid) {
      toast.error(t(`${tKey}errors.roomNameRequired`));
      return;
    }

    try {
      const payload = {
        name: roomForm.name,
        description: roomForm.description,
      };

      if (editingRoom) {
        await updateRoomRequest(editingRoom.roomId, payload);
        toast.success(t(`${tKey}success.roomUpdated`));
      } else {
        await createRoomRequest(activeRestaurantId, payload);
        toast.success(t(`${tKey}success.roomCreated`));
      }
      setIsRoomModalOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving room:", error);
      toast.error(t(`${tKey}errors.saveRoom`));
    }
  };

  const handleDeleteRoom = async (room) => {
    if (room.tables && room.tables.length > 0) {
      toast.error(t(`${tKey}errors.roomHasTables`));
      return;
    }

    if (window.confirm(t(`${tKey}confirm.deleteRoom`))) {
      try {
        await deleteRoomRequest(room.roomId);
        toast.success(t(`${tKey}success.roomDeleted`));
        fetchData();
      } catch (error) {
        console.error("Error deleting room:", error);
        toast.error(t(`${tKey}errors.deleteRoom`));
      }
    }
  };

  // Table handlers
  const openAddTableModal = (roomId = null) => {
    setEditingTable(null);
    const selectedRoom = roomId ? rooms.find((r) => r.roomId === roomId) : null;
    setTableForm({
      name: "",
      description: "",
      numberOfPlaces: 2,
      isActive: true,
      roomId: selectedRoom
        ? { value: selectedRoom.roomId, label: selectedRoom.name }
        : null,
    });
    setIsTableModalOpen(true);
  };

  const openEditTableModal = (table) => {
    setEditingTable(table);
    const selectedRoom = table.roomId
      ? rooms.find((r) => r.roomId === table.roomId)
      : null;
    setTableForm({
      name: table.name,
      description: table.description || "",
      numberOfPlaces: table.numberOfPlaces,
      isActive: table.isActive,
      roomId: selectedRoom
        ? { value: selectedRoom.roomId, label: selectedRoom.name }
        : null,
    });
    setIsTableModalOpen(true);
  };

  const handleSaveTable = async () => {
    if (!isTableFormValid) {
      toast.error(t(`${tKey}errors.tableNameRequired`));
      return;
    }

    try {
      const roomIdValue = tableForm.roomId?.value ?? tableForm.roomId ?? null;
      const payload = {
        name: tableForm.name,
        description: tableForm.description,
        numberOfPlaces: tableForm.numberOfPlaces,
        isActive: tableForm.isActive,
        roomId: roomIdValue,
      };

      if (editingTable) {
        await updateTableRequest(editingTable.tableId, payload);
        toast.success(t(`${tKey}success.tableUpdated`));
      } else {
        await createTableRequest(activeRestaurantId, payload);
        toast.success(t(`${tKey}success.tableCreated`));
      }
      setIsTableModalOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving table:", error);
      toast.error(t(`${tKey}errors.saveTable`));
    }
  };

  const handleDeleteTable = async (table) => {
    if (window.confirm(t(`${tKey}confirm.deleteTable`))) {
      try {
        await deleteTableRequest(table.tableId);
        toast.success(t(`${tKey}success.tableDeleted`));
        fetchData();
      } catch (error) {
        console.error("Error deleting table:", error);
        if (error.message?.includes("ACTIVE_ORDERS")) {
          toast.error(t(`${tKey}errors.tableHasActiveOrders`));
        } else {
          toast.error(t(`${tKey}errors.deleteTable`));
        }
      }
    }
  };

  const handleDuplicateTable = async (table) => {
    try {
      await duplicateTableRequest(table.tableId);
      toast.success(t(`${tKey}success.tableDuplicated`));
      fetchData();
    } catch (error) {
      console.error("Error duplicating table:", error);
      toast.error(t(`${tKey}errors.duplicateTable`));
    }
  };

  const handleToggleTableActive = async (table, e) => {
    e?.stopPropagation?.();
    try {
      await updateTableRequest(table.tableId, {
        isActive: !table.isActive,
      });
      toast.success(t(`${tKey}success.tableUpdated`));
      fetchData();
    } catch (error) {
      console.error("Error toggling table active:", error);
      toast.error(t(`${tKey}errors.saveTable`));
    }
  };

  // Get tables for a specific room (or without room)
  const getTablesForRoom = useCallback(
    (roomId) => {
      const room = sortedRooms.find(
        (r) => (r.roomId === null && roomId === null) || r.roomId === roomId,
      );
      const tables = (room?.tables || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      return hideInactive ? tables.filter((t) => t.isActive) : tables;
    },
    [sortedRooms, hideInactive],
  );

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Custom collision detection: isolates room drags from table drags
  const collisionDetection = useCallback((args) => {
    const activeId = String(args.active.id);

    if (activeId.startsWith("room-")) {
      // Dragging a room: only consider other room-* sortable items
      const filtered = {
        ...args,
        droppableContainers: args.droppableContainers.filter((c) =>
          String(c.id).startsWith("room-"),
        ),
      };
      return closestCenter(filtered);
    }

    if (activeId.startsWith("table-")) {
      // Dragging a table: first try table-* and container-room-* (ignore room-* sortables)
      const tableAndContainers = {
        ...args,
        droppableContainers: args.droppableContainers.filter((c) => {
          const id = String(c.id);
          return id.startsWith("table-") || id.startsWith("container-room-");
        }),
      };
      // Use pointerWithin first for precise detection, fall back to closestCenter
      const pw = pointerWithin(tableAndContainers);
      if (pw.length > 0) {
        // Prefer table items over containers for more precise positioning
        const tableHit = pw.find((c) => String(c.id).startsWith("table-"));
        return tableHit ? [tableHit] : [pw[0]];
      }
      const cc = closestCenter(tableAndContainers);
      if (cc.length > 0) return cc;

      // Fallback: include room-* items (for collapsed rooms without a visible container)
      return closestCenter(args);
    }

    return closestCenter(args);
  }, []);

  // Track the dragged table for DragOverlay rendering
  const handleDragStart = useCallback(
    (event) => {
      const activeId = String(event.active.id);
      if (activeId.startsWith("table-")) {
        const tableId = parseInt(activeId.replace("table-", ""));
        for (const room of sortedRooms) {
          const table = (room.tables || []).find((t) => t.tableId === tableId);
          if (table) {
            setActiveDragTable(table);
            return;
          }
        }
      }
    },
    [sortedRooms],
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragTable(null);
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
  }, []);

  // Unified drag end handler for both rooms and tables
  const handleDragEnd = useCallback(
    async (event) => {
      const { active, over } = event;

      setActiveDragTable(null);

      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
        expandTimerRef.current = null;
      }

      if (!over || active.id === over.id) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      // Room reordering
      if (activeId.startsWith("room-") && overId.startsWith("room-")) {
        const oldIndex = realRooms.findIndex(
          (r) => `room-${r.roomId}` === activeId,
        );
        const newIndex = realRooms.findIndex(
          (r) => `room-${r.roomId}` === overId,
        );
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

        const reordered = Array.from(realRooms);
        const [moved] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, moved);

        const roomIds = reordered.map((r) => r.roomId);

        const updatedRooms = rooms.map((room) => {
          const idx = roomIds.indexOf(room.roomId);
          if (idx !== -1) return { ...room, order: idx };
          return room;
        });
        setRooms(updatedRooms);

        try {
          await reorderRoomsRequest(activeRestaurantId, roomIds);
          toast.success(t(`${tKey}success.roomReordered`));
          fetchData();
        } catch (error) {
          console.error("Error reordering rooms:", error);
          toast.error(t(`${tKey}errors.reorderRooms`));
          fetchData();
        }
        return;
      }

      // Table drag (reorder within room or cross-room move)
      if (activeId.startsWith("table-")) {
        const tableId = parseInt(activeId.replace("table-", ""));
        const sourceRoomId = active.data?.current?.roomId ?? null;

        let targetRoomId;
        let targetIndex;

        if (overId.startsWith("table-")) {
          targetRoomId = over.data?.current?.roomId ?? null;
          const targetTables = getTablesForRoom(targetRoomId);
          targetIndex = targetTables.findIndex(
            (tb) => `table-${tb.tableId}` === overId,
          );
        } else if (overId.startsWith("container-room-")) {
          targetRoomId = over.data?.current?.roomId ?? null;
          targetIndex = 0;
        } else if (overId.startsWith("room-")) {
          const roomIdNum = parseInt(overId.replace("room-", ""));
          targetRoomId = isNaN(roomIdNum) ? null : roomIdNum;
          targetIndex = 0;
        } else {
          return;
        }

        const sourceKey = sourceRoomId ?? "none";
        const targetKey = targetRoomId ?? "none";

        if (sourceKey === targetKey) {
          // Same room: reorder
          const roomTables = getTablesForRoom(sourceRoomId);
          const oldIndex = roomTables.findIndex(
            (tb) => `table-${tb.tableId}` === activeId,
          );
          const newIndex = roomTables.findIndex(
            (tb) => `table-${tb.tableId}` === overId,
          );
          if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex)
            return;

          try {
            const reordered = Array.from(roomTables);
            const [moved] = reordered.splice(oldIndex, 1);
            reordered.splice(newIndex, 0, moved);
            const tableIds = reordered.map((tb) => tb.tableId);

            if (sourceRoomId === null) {
              await moveTableRequest(tableId, null, newIndex);
            } else {
              await reorderTablesRequest(sourceRoomId, tableIds);
            }
            toast.success(t(`${tKey}success.tableReordered`));
            fetchData();
          } catch (error) {
            console.error("Error reordering table:", error);
            toast.error(t(`${tKey}errors.moveTable`));
            fetchData();
          }
        } else {
          // Cross-room move
          try {
            await moveTableRequest(
              tableId,
              targetRoomId,
              targetIndex >= 0 ? targetIndex : 0,
            );
            toast.success(t(`${tKey}success.tableMoved`));
            fetchData();
          } catch (error) {
            console.error("Error moving table:", error);
            toast.error(t(`${tKey}errors.moveTable`));
            fetchData();
          }
        }
      }
    },
    [
      realRooms,
      rooms,
      activeRestaurantId,
      reorderRoomsRequest,
      getTablesForRoom,
      moveTableRequest,
      reorderTablesRequest,
      fetchData,
      t,
      tKey,
    ],
  );

  // Auto-expand collapsed rooms when dragging a table over them
  const handleDragOver = useCallback(
    (event) => {
      const { active, over } = event;

      if (!over) {
        if (expandTimerRef.current) {
          clearTimeout(expandTimerRef.current);
          expandTimerRef.current = null;
        }
        return;
      }

      const activeId = String(active.id);
      if (!activeId.startsWith("table-")) return;

      const overId = String(over.id);
      let targetKey = null;

      if (overId.startsWith("container-room-")) {
        const roomId = over.data?.current?.roomId;
        targetKey = roomId === null || roomId === undefined ? "noRoom" : roomId;
      } else if (overId.startsWith("room-")) {
        const roomIdNum = parseInt(overId.replace("room-", ""));
        targetKey = isNaN(roomIdNum) ? null : roomIdNum;
      }

      if (targetKey !== null && !expandedRooms[targetKey]) {
        if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
        expandTimerRef.current = setTimeout(() => {
          setExpandedRooms((prev) => ({ ...prev, [targetKey]: true }));
          expandTimerRef.current = null;
        }, 500);
      } else if (targetKey === null || expandedRooms[targetKey]) {
        if (expandTimerRef.current) {
          clearTimeout(expandTimerRef.current);
          expandTimerRef.current = null;
        }
      }
    },
    [expandedRooms],
  );

  // Room action buttons (add table, edit, delete)
  const renderRoomActions = (room) => {
    const actions = [
      {
        label: t(`${tKey}addTable`),
        onClick: () => openAddTableModal(room.roomId),
      },
    ];

    if (room.roomId != null) {
      actions.push(
        {
          label: t(`${tKey}editRoom`),
          onClick: () => openEditRoomModal(room),
        },
        {
          label: t(`${tKey}deleteRoom`),
          onClick: () => handleDeleteRoom(room),
        },
      );
    }

    if (isLtTablet()) {
      const hasTables = (room.tables?.length ?? 0) > 0;
      return (
        <div className="ml-auto" onClick={(e) => e.stopPropagation()}>
          <DropdownButton
            actions={actions}
            btnProps={{ size: "xs", variant: "transparent" }}
          >
            <FontAwesomeIcon
              icon={faEllipsis}
              className={hasTables ? "text-white" : "text-dark"}
            />
          </DropdownButton>
        </div>
      );
    }

    return (
      <div className="room-title__actions" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="success"
          size="xs"
          onClick={() => openAddTableModal(room.roomId)}
        >
          <FontAwesomeIcon icon={faPlus} />
        </Button>
        {room.roomId != null && (
          <>
            <Button
              variant="info"
              size="xs"
              onClick={() => openEditRoomModal(room)}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <Button
              variant="danger"
              size="xs"
              onClick={() => handleDeleteRoom(room)}
              disabled={deleteRoomLoading}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </>
        )}
      </div>
    );
  };

  // Table action buttons (duplicate, edit, delete) — always visible
  const renderTableActions = (table) => (
    <div className="flex">
      <Button
        variant="primary"
        size="xs"
        onClick={() => openEditTableModal(table)}
      >
        <FontAwesomeIcon icon={faEdit} />
      </Button>
      <Button
        variant="info"
        size="xs"
        onClick={() => handleDuplicateTable(table)}
        disabled={duplicateTableLoading}
        title={t(`${tKey}duplicateTable`)}
      >
        <FontAwesomeIcon icon={faCopy} />
      </Button>
      <Button
        variant="danger"
        size="xs"
        onClick={() => handleDeleteTable(table)}
        disabled={deleteTableLoading}
      >
        <FontAwesomeIcon icon={faTrash} />
      </Button>
    </div>
  );

  // Card view — improved Option A
  const renderTableCard = (
    table,
    index,
    { setNodeRef, style, attributes, listeners, isDragging },
  ) => (
    <Card
      ref={setNodeRef}
      style={style}
      className={`table-card ${
        !table.isActive ? "table-card--inactive" : ""
      } ${isDragging ? "table-card--dragging" : ""}`}
      flat
    >
      <span
        {...attributes}
        {...listeners}
        className={`table-card__handle ${table.isActive ? "table-card__handle--active" : "table-card__handle--inactive"}`}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} size="lg" />
      </span>
      <div className="table-card__content">
        <div className="flex align-center gap-2 mb-1">
          <span className="table-card__name">{table.name}</span>
          <span className="ml-auto">
            <Tag
              text={`${table.numberOfPlaces} ${t(`${tKey}places`)}`}
              variant="pastel-medium"
            />
          </span>
        </div>
        <div className="table-card__desc">{table.description || "\u00A0"}</div>
        <div className="table-card__footer">
          <span
            role="button"
            tabIndex={0}
            className={`cursor-pointer mr-1 ${updateTableLoading ? "opacity-50 pointer-events-none" : ""}`}
            onClick={(e) => handleToggleTableActive(table, e)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleToggleTableActive(table, e);
              }
            }}
          >
            <Tag
              text={table.isActive ? "active" : "inactive"}
              variant={table.isActive ? "success" : "pastel-medium"}
              tKey={tKey}
            />
          </span>
          {renderTableActions(table)}
        </div>
      </div>
    </Card>
  );

  // List view — improved Option B
  const renderTableRow = (
    table,
    index,
    { setNodeRef, style, attributes, listeners, isDragging },
  ) => (
    <div
      ref={setNodeRef}
      style={style}
      className={`table-row ${!table.isActive ? "table-row--inactive" : ""} ${
        isDragging ? "table-row--dragging" : ""
      } ${index % 2 === 1 ? "table-row--striped" : ""}`}
    >
      <span
        {...attributes}
        {...listeners}
        className={`table-row__handle ${table.isActive ? "table-row__handle--active" : "table-row__handle--inactive"}`}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} size="lg" />
      </span>
      <div className="table-row__body">
        <div className="table-row__line">
          <span className="table-row__name">{table.name}</span>
          <span>
            <Tag
              text={`${table.numberOfPlaces} ${t(`${tKey}places`)}`}
              variant="pastel-medium"
            />
          </span>
        </div>
        <div className="table-row__desc">{table.description || "\u00A0"}</div>
        <div className="table-row__line table-row__line--end">
          <span
            role="button"
            tabIndex={0}
            className={`table-row__status ${updateTableLoading ? "opacity-50 pointer-events-none" : ""}`}
            onClick={(e) => handleToggleTableActive(table, e)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleToggleTableActive(table, e);
              }
            }}
          >
            <Tag
              text={table.isActive ? "active" : "inactive"}
              variant={table.isActive ? "success" : "pastel-medium"}
              tKey={tKey}
            />
          </span>
          <div className="table-row__actions">{renderTableActions(table)}</div>
        </div>
      </div>
    </div>
  );

  // Render tables in the chosen view mode (no DndContext here — unified context wraps all rooms)
  const renderTables = (tables, roomId) => {
    const sortedTables = [...(tables || [])].sort((a, b) => a.order - b.order);
    const isCardView = effectiveViewMode === "card";
    const tableIds = sortedTables.map((t) => `table-${t.tableId}`);

    return (
      <SortableContext
        items={tableIds}
        strategy={
          isCardView ? rectSortingStrategy : verticalListSortingStrategy
        }
      >
        <div
          className={`${isCardView ? "table-grid" : "table-list"} droppable-area`}
        >
          {sortedTables.length === 0 ? (
            <p className="text-muted text-center p-2 w-100">
              {t(`${tKey}noTablesInRoom`)}
            </p>
          ) : (
            sortedTables.map((table, index) => (
              <SortableTableItem
                key={table.tableId}
                id={`table-${table.tableId}`}
                roomId={roomId}
              >
                {(dndProps) =>
                  isCardView
                    ? renderTableCard(table, index, dndProps)
                    : renderTableRow(table, index, dndProps)
                }
              </SortableTableItem>
            ))
          )}
        </div>
      </SortableContext>
    );
  };

  // Room collapse title with count and total places
  const getRoomTitle = (room) => {
    const tables = getTablesForRoom(room.roomId);
    const totalPlaces = tables.reduce(
      (sum, table) => sum + (table.numberOfPlaces || 0),
      0,
    );
    return (
      <div className="room-title">
        <div className="room-title__info">
          <span className="font-weight-bold">{room.name}</span>
          <span className="text-muted room-title__stats">
            ({tables.length} {t(`${tKey}tables`)}, {totalPlaces}{" "}
            {t(`${tKey}places`)})
          </span>
        </div>
        {renderRoomActions(room)}
      </div>
    );
  };

  // "Without room" collapse title
  const getNoRoomTitle = (room) => {
    const tables = getTablesForRoom(room.roomId);
    const totalPlaces = tables.reduce(
      (sum, table) => sum + (table.numberOfPlaces || 0),
      0,
    );
    return (
      <div className="room-title">
        <div className="room-title__info">
          <span className="font-weight-bold">{t(`${tKey}withoutRoom`)}</span>
          <span className="text-muted room-title__stats">
            ({tables.length} {t(`${tKey}tables`)}, {totalPlaces}{" "}
            {t(`${tKey}places`)})
          </span>
        </div>
        {renderRoomActions(room)}
      </div>
    );
  };

  const hasNoContent = !sortedRooms.some((r) => (r.tables?.length ?? 0) > 0);

  if (!activeRestaurantId) {
    return null;
  }

  if (overviewLoading) {
    return (
      <div className="dashboard-content flex justify-center align-center p-5">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="dashboard-content tables-management">
      <div className="bg-white rounded p-3">
        <div className="tables-toolbar">
          <div className="tables-toolbar__left">
            <div className="tables-toolbar__toggles">
              {!isLtTablet() && (
                <div className="flex">
                  <Button
                    variant={viewMode === "card" ? "info" : "white"}
                    icon
                    onClick={() => setViewMode("card")}
                  >
                    <FontAwesomeIcon icon={faTableCellsLarge} />
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "info" : "white"}
                    icon
                    onClick={() => setViewMode("list")}
                  >
                    <FontAwesomeIcon icon={faList} />
                  </Button>
                </div>
              )}
              <Checkbox
                className="border-dark"
                icon={
                  <div>
                    <FontAwesomeIcon
                      icon={faCheck}
                      size="sm"
                      className="text-dark"
                    />
                  </div>
                }
                label={t(`${tKey}hideInactive`)}
                checked={hideInactive}
                onChange={(checked) => setHideInactive(checked)}
              />
            </div>
          </div>
          <div className="tables-toolbar__actions">
            <Button
              variant="primary"
              size={isLtTablet() ? "xs" : "sm"}
              onClick={openAddRoomModal}
              disabled={createRoomLoading}
              title={t(`${tKey}addRoom`)}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              {t(`${tKey}addRoom`)}
            </Button>
            <Button
              variant="primary"
              size={isLtTablet() ? "xs" : "sm"}
              onClick={() => openAddTableModal(null)}
              disabled={createTableLoading}
              title={t(`${tKey}addTable`)}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              {t(`${tKey}addTable`)}
            </Button>
          </div>
        </div>

        {hasNoContent ? (
          <div className="text-center p-5 text-muted">
            <FontAwesomeIcon icon={faChair} size="3x" className="mb-3" />
            <p>{t(`${tKey}noRooms`)}</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={realRooms.map((r) => `room-${r.roomId}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="rooms-list">
                {sortedRooms.map((room) => {
                  const isUnassigned = room.roomId === null;
                  const key = isUnassigned ? "noRoom" : room.roomId;
                  const hasTables = (room.tables?.length ?? 0) > 0;
                  const visibleTables = getTablesForRoom(room.roomId);

                  if (hideInactive && visibleTables.length === 0) return null;

                  if (isUnassigned) {
                    return (
                      <DroppableRoom key={key} roomId={room.roomId}>
                        <Collapse
                          title={getNoRoomTitle(room)}
                          variant={hasTables ? "pastel-medium" : "pastel-light"}
                          initialStateOpen={hasTables}
                          isOpen={expandedRooms[key]}
                          onToggle={() => toggleRoom(key)}
                          className="mb-2"
                        >
                          <div className="p-2">
                            {renderTables(visibleTables, room.roomId)}
                          </div>
                        </Collapse>
                      </DroppableRoom>
                    );
                  }

                  return (
                    <SortableRoomItem key={key} id={`room-${room.roomId}`}>
                      {({
                        setNodeRef,
                        style,
                        attributes,
                        listeners,
                        isDragging,
                      }) => (
                        <div
                          ref={setNodeRef}
                          style={style}
                          className={`mb-2 ${isDragging ? "room-dragging" : ""}`}
                        >
                          <DroppableRoom roomId={room.roomId}>
                            <Collapse
                              title={getRoomTitle(room)}
                              variant={
                                hasTables ? "pastel-medium" : "pastel-light"
                              }
                              initialStateOpen={hasTables}
                              isOpen={expandedRooms[key]}
                              onToggle={() => toggleRoom(key)}
                              isDroppable
                              dragHandleProps={{ ...attributes, ...listeners }}
                            >
                              <div className="p-2">
                                {renderTables(visibleTables, room.roomId)}
                              </div>
                            </Collapse>
                          </DroppableRoom>
                        </div>
                      )}
                    </SortableRoomItem>
                  );
                })}
              </div>
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeDragTable ? (
                effectiveViewMode === "card" ? (
                  <Card
                    className={`table-card ${!activeDragTable.isActive ? "table-card--inactive" : ""}`}
                    flat
                  >
                    <span className="table-card__handle table-card__handle--active">
                      <FontAwesomeIcon icon={faEllipsisVertical} size="lg" />
                    </span>
                    <div className="table-card__content">
                      <div className="flex align-center gap-2 mb-1">
                        <span className="table-card__name">
                          {activeDragTable.name}
                        </span>
                        <span className="ml-auto">
                          <Tag
                            text={`${activeDragTable.numberOfPlaces} ${t(`${tKey}places`)}`}
                            variant="pastel-medium"
                          />
                        </span>
                      </div>
                      <div className="table-card__desc">
                        {activeDragTable.description || "\u00A0"}
                      </div>
                    </div>
                  </Card>
                ) : (
                  <div className="table-row">
                    <span className="table-row__handle table-row__handle--active">
                      <FontAwesomeIcon icon={faEllipsisVertical} size="lg" />
                    </span>
                    <div className="table-row__body">
                      <div className="table-row__line">
                        <span className="table-row__name">
                          {activeDragTable.name}
                        </span>
                        <span>
                          <Tag
                            text={`${activeDragTable.numberOfPlaces} ${t(`${tKey}places`)}`}
                            variant="pastel-medium"
                          />
                        </span>
                      </div>
                      <div className="table-row__desc">
                        {activeDragTable.description || "\u00A0"}
                      </div>
                    </div>
                  </div>
                )
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Room Modal */}
      <Modal
        isOpen={isRoomModalOpen}
        onClose={() => setIsRoomModalOpen(false)}
        noBgClickable
        noFooter
      >
        <div
          className="p-3"
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              e.target.tagName !== "TEXTAREA" &&
              !createRoomLoading &&
              !updateRoomLoading &&
              isRoomFormValid
            ) {
              e.preventDefault();
              handleSaveRoom();
            }
          }}
        >
          <h3 className="mt-0 mb-3">
            {editingRoom ? t(`${tKey}editRoom`) : t(`${tKey}addRoom`)}
          </h3>
          <Form
            fields={roomFields}
            currentForm={roomForm}
            setFormDetails={setRoomForm}
            setIsValid={setIsRoomFormValid}
            fieldClass="bg-pastel-light"
          />
          <div className="flex justify-end gap-1 mt-3">
            <Button variant="primary" onClick={() => setIsRoomModalOpen(false)}>
              {t("utils.common.cancel")}
            </Button>
            <Button
              variant="success"
              onClick={handleSaveRoom}
              disabled={
                createRoomLoading || updateRoomLoading || !isRoomFormValid
              }
            >
              {t("utils.common.save")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Table Modal */}
      <Modal
        isOpen={isTableModalOpen}
        onClose={() => setIsTableModalOpen(false)}
        noBgClickable
        noFooter
      >
        <div
          className="p-3"
          onKeyDown={(e) => {
            const isDescription = e.target.tagName === "TEXTAREA";
            const isInDropdown = e.target.closest?.("[class*='react-select']");
            if (
              e.key === "Enter" &&
              !isDescription &&
              !isInDropdown &&
              !createTableLoading &&
              !updateTableLoading &&
              isTableFormValid
            ) {
              e.preventDefault();
              handleSaveTable();
            }
          }}
        >
          <h3 className="mt-0 mb-3">
            {editingTable ? t(`${tKey}editTable`) : t(`${tKey}addTable`)}
          </h3>
          <Form
            fields={tableFields}
            currentForm={tableForm}
            setFormDetails={setTableForm}
            setIsValid={setIsTableFormValid}
            fieldClass="bg-pastel-light"
          />
          <div className="flex justify-end gap-1 mt-2">
            <Button
              variant="primary"
              onClick={() => setIsTableModalOpen(false)}
            >
              {t("utils.common.cancel")}
            </Button>
            <Button
              variant="success"
              onClick={handleSaveTable}
              disabled={
                createTableLoading || updateTableLoading || !isTableFormValid
              }
            >
              {t("utils.common.save")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RestaurantTables;
